import { z } from "zod";
import type { PlanStep, WorkPlan } from "../../../skills/leadcue-reply/scripts/lib/work-plan.ts";

/*
 * A conversation the app is working on (docs/2026-10-05-in-flight-visibility.md). When a rep answers
 * the last question on a conversation, drafting resumes and for some minutes there is no draft. The
 * row used to leave "Your move" for that time, filed as done, and a run that died there looked the
 * same as one still working: an absence. Here the wait is a state of its own, read from the record
 * and never from a watcher: the rep's last answer, with nothing the app produced after it.
 */

/** A step still waiting on someone, as the plan stores it: the run's open work. */
export const openStep = (step: Pick<PlanStep, "state">): boolean =>
  ["pending", "invalidated"].includes(step.state);

/**
 * The plan as it is shown while a new attempt is written (w563): without the open work the attempt
 * replaces, in the open conversation (`web/draft-again.tsx`) and in its inbox row alike.
 */
export function withoutOpenWork<T extends Pick<WorkPlan, "steps">>(plan: T): T {
  return { ...plan, steps: plan.steps.filter((step) => !openStep(step)) };
}

/** As the repository reads it: the rep's newest answer that nothing has followed. */
export const InFlightRunSchema = z.object({
  /** When the rep answered: the moment drafting was asked to resume. */
  since: z.string(),
  /** The job that carries the answer to the run ended as failed, after its retries. */
  failed: z.boolean(),
});
export type InFlightRun = z.infer<typeof InFlightRunSchema>;

/**
 * How long a draft may take before the row stops saying "drafting" and says none came. Null: no
 * limit is applied. It stays null until the time from an answer to its draft has been measured on
 * real runs; a guessed number would call a healthy run stuck (one real run took 6 min 47 s) or
 * leave a dead one looking alive. The one place to set it.
 */
export const DRAFT_STALL_AFTER_MS: number | null = null;

export type InFlightView = {
  /**
   * `stalled` is reachable only once `DRAFT_STALL_AFTER_MS` is set. `redrafting` is a new attempt a
   * rep asked for with "Draft again" (docs/2026-10-12-draft-again.md); only the Work panel draws
   * it, from that control's offer (`web/draft-again.tsx`, `useRedraft`), never `inFlightView`.
   */
  kind: "working" | "stalled" | "failed" | "redrafting";
  since: string;
};

const FINISHED = new Set(["completed", "reported", "skipped"]);

/**
 * Nothing on the plan is the rep's to do and no draft exists: every step is settled, or the plan
 * holds none yet. Only then can an unanswered resume mean "the app is writing".
 */
export function awaitsDraft(plan: Pick<WorkPlan, "disposition" | "steps">): boolean {
  if (plan.disposition !== "active") return false;
  if (plan.steps.some((step) => step.input.kind === "reply")) return false;
  return plan.steps.every((step) => FINISHED.has(step.state));
}

export function inFlightView(
  plan: Pick<WorkPlan, "disposition" | "steps">,
  run: InFlightRun | null | undefined,
  now: Date,
  stallAfterMs: number | null = DRAFT_STALL_AFTER_MS,
): InFlightView | null {
  if (!run || !awaitsDraft(plan)) return null;
  if (run.failed) return { kind: "failed", since: run.since };
  const waited = now.getTime() - Date.parse(run.since);
  if (stallAfterMs !== null && waited >= stallAfterMs) return { kind: "stalled", since: run.since };
  return { kind: "working", since: run.since };
}

/** The row's chip. Its time is shown by the row, as a clock time, never as "a moment ago". */
export const IN_FLIGHT_CHIP = {
  working: { label: "Drafting", tone: "slate" },
  stalled: { label: "No draft yet", tone: "amber" },
  failed: { label: "No draft came", tone: "amber" },
  redrafting: { label: "Drafting again", tone: "slate" },
} as const;

/**
 * The Work panel's words, in the rep's terms: what the app is doing, since when, that nothing was
 * sent, and the one way out. `when` renders the start as a clock time.
 */
export function inFlightCard(view: InFlightView, when: (at: string) => string) {
  const started = when(view.since);
  if (view.kind === "redrafting")
    return {
      title: "Drafting again",
      body: `A new draft was asked for ${started}. Nothing has been sent. It appears here when it is ready; you can leave this page.`,
      action: "Write the reply yourself instead",
    };
  if (view.kind === "working")
    return {
      title: "Writing the draft",
      body: `Started ${started}, when you answered. Nothing has been sent. The draft appears here when it is ready; you can leave this page.`,
      action: "Write the reply yourself instead",
    };
  if (view.kind === "stalled")
    return {
      title: "No draft yet",
      body: `Drafting started ${started} and no draft has arrived. Nothing has been sent. A draft may still come; if this is urgent, write the reply yourself.`,
      action: "Write the reply yourself",
    };
  return {
    title: "No draft came",
    body: `Drafting started ${started} and stopped before a draft was written. Nothing has been sent, and nothing will be written unless you write it.`,
    action: "Write the reply yourself",
  };
}
